import { useState, useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import { SectionHeader } from "@/components/landing/shared";
import { useLeadModal } from "@/components/landing/modal-context";
import { START_CTA } from "@/config";

const B = "/ad-creatives";

// Proven ad-creative examples (the swipe file we launch from) with illustrative performance chips.
const ADS = [
  { img: `${B}/roofing-1.webp`, alt: "Free roofing consultation ad", stat: "247", statLabel: "leads this week", cpl: "$18" },
  { img: `${B}/med-spa.webp`, alt: "Med spa treatment promo ad", stat: "367", statLabel: "leads generated", cpl: "$16" },
  { img: `${B}/furnace.webp`, alt: "Furnace install offer ad", stat: "156", statLabel: "leads this week", cpl: "$27" },
  { img: `${B}/cleaning.webp`, alt: "Residential cleaning services ad", stat: "612", statLabel: "leads this week", cpl: "$11" },
  { img: `${B}/garage-tuneup.webp`, alt: "Garage door tune-up ad", stat: "98", statLabel: "leads this week", cpl: "$14" },
  { img: `${B}/pest-control.webp`, alt: "Pest control free inspection ad", stat: "452", statLabel: "leads this week", cpl: "$19" },
  { img: `${B}/ac.webp`, alt: "New AC system offer ad", stat: "389", statLabel: "leads generated", cpl: "$21" },
  { img: `${B}/landscaping.webp`, alt: "Landscaping seasonal offer ad", stat: "289", statLabel: "leads this week", cpl: "$18" },
  { img: `${B}/plumbing.webp`, alt: "Plumbing emergency service ad", stat: "198", statLabel: "leads this week", cpl: "$20" },
  { img: `${B}/home-inspection.webp`, alt: "Free home inspection ad", stat: "203", statLabel: "leads this week", cpl: "$19" },
  { img: `${B}/solar-offer.webp`, alt: "Solar and battery offer ad", stat: "892", statLabel: "leads generated", cpl: "$29" },
  { img: `${B}/garage-repair.webp`, alt: "Garage door repair discount ad", stat: "147", statLabel: "leads this week", cpl: "$12" },
  { img: `${B}/body-shop.webp`, alt: "Auto body collision repair ad", stat: "276", statLabel: "leads generated", cpl: "$15" },
  { img: `${B}/adu.webp`, alt: "ADU backyard build offer ad", stat: "418", statLabel: "leads this week", cpl: "$27" },
  { img: `${B}/real-estate.webp`, alt: "Real estate buyer lead ad", stat: "734", statLabel: "leads this week", cpl: "$33" },
  { img: `${B}/insurance.webp`, alt: "Insurance quote lead-gen ad", stat: "1,043", statLabel: "leads generated", cpl: "$10" },
  { img: `${B}/roofing-2.webp`, alt: "New roofs pricing ad", stat: "84", statLabel: "leads this week", cpl: "$24" },
  { img: `${B}/gym.webp`, alt: "Gym free trial claim ad", stat: "534", statLabel: "leads generated", cpl: "$8" },
  { img: `${B}/pool.webp`, alt: "Pool remodel offer ad", stat: "63", statLabel: "leads this week", cpl: "$67" },
  { img: `${B}/solar.webp`, alt: "Solar installation lead ad", stat: "521", statLabel: "leads generated", cpl: "$21" },
];

const AdCard = ({ ad, lazy = false }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <figure aria-hidden={lazy || undefined}
      className="group relative w-[150px] shrink-0 overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-soft transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-soft-lg sm:w-[188px] lg:w-[220px]">
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-indigo-100 to-slate-200">
        {/* Both copies lazy — the wall is below the fold, so phones don't decode
            40 images during page load (the old Meta-offer carousel problem).
            Gradient skeleton covers the brief pop-in as it scrolls into view. */}
        <img
          src={ad.img}
          alt={lazy ? "" : ad.alt}
          width={440}
          height={440}
          loading="lazy"
          fetchpriority="low"
          decoding="async"
          ref={(n) => { if (n && n.complete && n.naturalWidth > 0) setLoaded(true); }}
          onLoad={() => setLoaded(true)}
          className={`relative h-full w-full object-cover transition-opacity duration-500 ease-out group-hover:scale-[1.06] ${loaded ? "opacity-100" : "opacity-0"}`}
        />
      <span className="absolute left-2 top-2 rounded-md bg-black/60 px-1.5 py-0.5 font-mono text-[8.5px] font-medium uppercase tracking-wider text-white">
        Sponsored
      </span>
      {/* gradient hover ring */}
      <span className="pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-inset ring-transparent transition-colors duration-500 group-hover:ring-brand-indigo/30" />
    </div>
    <figcaption className="flex items-center justify-between gap-2 px-3 py-2.5">
      <div className="leading-none">
        <p className="font-display text-[15px] font-bold text-navy sm:text-base">{ad.stat}</p>
        <p className="mt-1 font-mono text-[8.5px] uppercase tracking-wide text-navy/45 sm:text-[9.5px]">{ad.statLabel}</p>
      </div>
      <div className="h-7 w-px bg-navy/10" />
      <div className="text-right leading-none">
        <p className="font-display text-[15px] font-bold text-gradient sm:text-base">{ad.cpl}</p>
        <p className="mt-1 font-mono text-[8.5px] uppercase tracking-wide text-navy/45 sm:text-[9.5px]">cost / lead</p>
      </div>
    </figcaption>
    </figure>
  );
};

function Row({ ads, direction, dur, play }) {
  // Pure-CSS marquee (translateX 0 -> -50% loop) — compositor-only animation, no
  // JS rAF on the main thread (that was the mobile scroll jank). Two copies of
  // the set make the -50% loop seamless; the duplicate copy lazy-loads.
  const cards = [...ads, ...ads];
  return (
    <div className="overflow-hidden">
      <div
        className="animate-marquee flex w-max items-stretch gap-3 pr-3 sm:gap-4 sm:pr-4"
        style={{
          animationDuration: `${dur}s`,
          animationDirection: direction === "right" ? "reverse" : "normal",
          animationPlayState: play ? "running" : "paused",
        }}
      >
        {cards.map((ad, i) => (
          <AdCard key={i} ad={ad} lazy={i >= ads.length} />
        ))}
      </div>
    </div>
  );
}

export default function AdsThatWin({ ctaHref = "", ctaLabel = "", ctaOnClick = null }) {
  // Outside LeadModalProvider (new site) openModal is a safe no-op; the CTA
  // renders as a link when ctaHref is set, so the modal is never needed there.
  const { openModal } = useLeadModal();
  const half = Math.ceil(ADS.length / 2);
  const rowA = ADS.slice(0, half);
  const rowB = ADS.slice(half);

  // Pause the marquee whenever it's off-screen so it never burns compositor
  // frames while the visitor is reading other sections (fixes scroll jank).
  // The cards themselves are always rendered + eager-loaded, so nothing pops in.
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    let fired = false;
    const io = new IntersectionObserver(
      ([entry]) => { fired = true; setInView(entry.isIntersecting); },
      { rootMargin: "0px 0px 100px 0px" }
    );
    io.observe(el);
    // In-app browsers (Instagram/Facebook) may not fire IO until a scroll —
    // reveal/animate anyway so the wall never sits frozen on a still page.
    const fallback = setTimeout(() => { if (!fired) setInView(true); }, 1200);
    return () => { io.disconnect(); clearTimeout(fallback); };
  }, []);

  return (
    <section ref={sectionRef} id="ads" data-testid="ads-that-win-section" className="relative overflow-hidden bg-white py-16 sm:py-24">
      <div className="mx-auto mb-10 max-w-6xl px-5 sm:mb-12 sm:px-8">
        <SectionHeader
          eyebrow="The ad engine"
          title={<>Start with ads that <span className="text-gradient">already win.</span></>}
          subtitle="We never start from a blank page. Your campaigns launch with proven, scroll-stopping creatives — matched to your trade and built to turn cheap clicks into booked jobs."
        />
      </div>

      {/* FULL-BLEED two-row scrolling wall */}
      <div className="relative space-y-3 sm:space-y-4">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-white to-transparent sm:w-32" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-white to-transparent sm:w-32" />
        <Row ads={rowA} direction="left" dur={95} play={inView} />
        <Row ads={rowB} direction="right" dur={110} play={inView} />
      </div>

      <div className="mx-auto mt-12 flex max-w-6xl flex-col items-center gap-3 px-5 sm:px-8">
        {ctaOnClick ? (
          <button
            type="button"
            onClick={ctaOnClick}
            data-testid="ads-cta"
            className="group inline-flex cursor-pointer items-center justify-center gap-2 rounded-full bg-brand-gradient px-7 py-4 font-display text-base font-semibold text-white shadow-pill transition-transform duration-300 hover:-translate-y-0.5"
          >
            {ctaLabel || "Book a free call"}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        ) : ctaHref ? (
          <a
            href={ctaHref}
            data-testid="ads-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-gradient px-7 py-4 font-display text-base font-semibold text-white shadow-pill transition-transform duration-300 hover:-translate-y-0.5"
          >
            {ctaLabel || "Book a free call"}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        ) : (
          <button
            type="button"
            onClick={openModal || undefined}
            data-testid="ads-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-gradient px-7 py-4 font-display text-base font-semibold text-white shadow-pill transition-transform duration-300 hover:-translate-y-0.5"
          >
            {START_CTA}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        )}
        <p className="font-mono text-[11px] uppercase tracking-wide text-navy/45">
          Example creatives · Your ads are custom-built for your business
        </p>
      </div>
    </section>
  );
}
